import React, { useState, useEffect, useRef } from "react";
import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast, Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import { formSchema, FormSchema } from "./utils/validation";
import { ProfileFormData, ClientType } from "./types/form";
import { ProgressBar } from "./components/UI/ProgressBar";
import { LoadingModal } from "./components/UI/LoadingModal";
import { GradientButton } from "./components/UI/GradientButton";
import { ErrorDialog } from "./components/UI/ErrorDialog";
import { EmailExistsModal } from "./components/UI/EmailExistsModal";
import { ProfileExistsModal } from "./components/UI/ProfileExistsModal";
import { OrganizationExistsModal } from "./components/UI/OrganizationExistsModal";
import { LoginModal } from "./components/UI/LoginModal";
import { useAuth } from "./context/AuthContext";
import { login as apiLogin, getLocation, setAuthToken } from "./utils/api";
import { CheckoutForm, Return } from "./components/UI/Payment";
// merged into AboutMe - SocialMedia component is now integrated into AboutMe
import { AboutMe } from "./components/FormSteps/AboutMe";
import { Reviews } from "./components/FormSteps/Reviews";
import { Customization } from "./components/FormSteps/Customization";
import { TemplateSelection } from "./components/FormSteps/TemplateSelection";
import { PaymentStep } from "./components/FormSteps/Payment";
import { SuccessStep } from "./components/FormSteps/Success";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { useAutoSave } from "./hooks/useAutoSave";
import { AutoSaveIndicator } from "./components/UI/AutoSaveIndicator";
import { getClientConfig } from "./utils/clientConfig";
import {
  submitProfile,
  fetchExistingProfile,
  checkExistingProfile,
} from "./utils/api";
import { generateVCard, generateSharingMetadata } from "./utils/formatters";
import { TemplatePreviewModal } from "./components/UI/TemplatePreviewModal";

const App: React.FC = () => {
  const { isAuthenticated, user, token, login: setAuth, logout } = useAuth();
  useEffect(() => {
    setAuthToken(token || null);
  }, [token]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const [showProfileExistsModal, setShowProfileExistsModal] = useState(false);
  const [showOrganizationExistsModal, setShowOrganizationExistsModal] =
    useState(false);
  const [showDataConflictModal, setShowDataConflictModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [conflictingEmail, setConflictingEmail] = useState("");
  const [autosaveStatus, setAutosaveStatus] = useState<
    "idle" | "saving" | "saved"
  >("idle");
  const emailCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showPayment, setShowPayment] = useState(false); // Set to false for normal flow
  const [paymentKey, setPaymentKey] = useState(1);
  // Payment is compulsory for new users, but not for editing users
  const [includePaymentStep] = useState(!isAuthenticated);
  const [clientType] = useState<ClientType>("Default");
  const [customerEmail, setCustomerEmail] = useState("");
  const [hasHandledSuccess, setHasHandledSuccess] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    title: string;
    message: string;
    error?: string;
    details?: string;
  }>({
    title: "Error",
    message: "An error occurred",
  });

  const [isEmailBeingChanged, setIsEmailBeingChanged] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const handleClosePreview = () => {
    setShowPreview(false);
  };
  
  const handleOpenPreview = () => {
    setShowPreview(true);
  };



  // Helper function to handle form validation errors
  const handleFormValidationErrors = (
    errors: any,
    title: string = "Please Fix These Issues",
    messagePrefix: string = "Found",
    messageSuffix: string = "that need attention.",
  ) => {
    const allErrors = Object.keys(errors)
      .map((key) => {
        const error = errors[key as keyof FormSchema];
        if (error?.message) {
          // Provide more helpful error messages for URL fields
          if (
            key === "website" ||
            key.includes("socialMedia") ||
            key.includes("link")
          ) {
            return `${key} | ${JSON.stringify(errors)} - ${error.message}\n💡 Tip: Add the full URL including "https://" or "http://"`;
          }
          return error.message;
        }
        return null;
      })
      .filter(Boolean);

    // Find the first step with validation errors and navigate to it
    const firstInvalidStep = findFirstInvalidStep(errors);
    if (firstInvalidStep !== currentStep) {
      // Add a small delay to ensure the error dialog is shown before navigation
      setTimeout(() => {
        stepNavigation.goToStep(firstInvalidStep);
      }, 100);
    }

    setErrorDetails({
      title,
      message: `${messagePrefix} ${allErrors.length} field${
        allErrors.length === 1 ? "" : "s"
      } ${messageSuffix}`,
      details: allErrors.join("\n\n"),
    });
    setShowErrorDialog(true);
  };

  const stepNavigation = useStepNavigation(includePaymentStep, clientType, isAuthenticated);
  const {
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    canGoNext,
    canGoPrev,
    resetToFirstStep,
  } = stepNavigation;

  const methods = useForm<FormSchema>({
    resolver: zodResolver(formSchema) as unknown as Resolver<FormSchema>,
    defaultValues: {
      firstName: "",
      lastName: "",
      title: "",
      organizationName: "",
      headshot: null,
      businessLogo: null,
      wasReferred: undefined,
      referralName: "",
      email: "",
      phone: "",
      website: "",
      socialMedia: {},
      aboutMeText: "",
      service: "",
      primaryColor: "#5855f8",
      secondaryColor: "#ee4a8c",
      buttonColor: "#42BCF5",
      qrCodeLogo: null,
      fabButtonLogo: null,
      faviconLogo: null,
      selectedTemplate: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = methods;
  const formData = watch();

  // Ensure selectedTemplate is populated from templateExhibitId when missing
  const resolveSelectedTemplateFromExhibitId = (
    exhibitId?: string,
    currentSelectedTemplate?: string,
  ) => {
    if (
      !exhibitId ||
      (currentSelectedTemplate && currentSelectedTemplate.trim() !== "")
    ) {
      return;
    }

    const juliaExhibitId = String(process.env.REACT_APP_JULIA_EXHIBIT_ID || "");
    const johnExhibitId = String(process.env.REACT_APP_JOHN_EXHIBIT_ID || "");
    const charleyExhibitId = String(
      process.env.REACT_APP_CHARLEY_EXHIBIT_ID || "",
    );
    const templateId = String(process.env.REACT_APP_TEMPLATE_ID_LOCATION || "");

    let resolvedName: string | undefined;
    if (exhibitId === juliaExhibitId) {
      resolvedName = "Modern Professional";
    } else if (exhibitId === johnExhibitId) {
      resolvedName = "Confident and Bold";
    } else if (exhibitId === charleyExhibitId) {
      resolvedName = "Circle Pro";
    }

    if (resolvedName) {
      setValue("selectedTemplate" as any, resolvedName as any);
      if (templateId) {
        setValue("template" as any, templateId as any);
      }
    }
  };

  // Auto-save functionality
  const { loadFromLocalStorage, clearSavedData, saveToServer } = useAutoSave(
    formData as ProfileFormData,
    (email: string) => {
      setConflictingEmail(email);
      setShowEmailExistsModal(true);
    },
    (email: string) => {
      setConflictingEmail(email);
      setShowOrganizationExistsModal(true);
    },
    showDataConflictModal, // Only prevent server save when data conflict modal is showing
    setAutosaveStatus, // Pass the status setter for autosave indicator
    isEmailBeingChanged,
  );

  // Check for existing profile when email changes (qualified email)
  useEffect(() => {
    if (formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      // Clear payment data if email has changed from stored customerEmail
      const storedCustomerEmail = localStorage.getItem("customerEmail");
      if (storedCustomerEmail && storedCustomerEmail !== formData.email) {
        localStorage.removeItem("paymentStatus");
        localStorage.removeItem("customerEmail");
      }

      // Clear any existing timeout
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }

      // Debounce the email check to avoid checking on every keystroke
      const timeout = setTimeout(() => {
        try {
          // Only check for existing profile if we have local data to compare
          const localData = localStorage.getItem("profileFormData");
          const localDataParsed = localData ? JSON.parse(localData) : {};

          // Only proceed if we have meaningful local data (not just empty object)
          const hasLocalData =
            Object.keys(localDataParsed).length > 0 &&
            localDataParsed.email === formData.email;

          if (hasLocalData) {
            checkExistingProfile(formData.email)
              .then((existingProfile) => {
                // Only show modal if there's actually an existing profile on the server
                // AND it's different from local data
                if (
                  existingProfile &&
                  Object.keys(existingProfile).length > 0
                ) {
                  // Check if this is a substantial existing profile (has more than just basic fields)
                  // This helps distinguish between truly existing profiles and recently created ones
                  const hasSubstantialData =
                    existingProfile.firstName ||
                    existingProfile.lastName ||
                    existingProfile.organizationName ||
                    existingProfile.phone ||
                    existingProfile.aboutMeText ||
                    (existingProfile.socialMedia &&
                      Object.values(existingProfile.socialMedia).some(
                        (val) => typeof val === "string" && val.trim() !== "",
                      ));

                  // Only show modal if profile has substantial data AND data is different
                  if (hasSubstantialData) {
                    const isDifferent =
                      JSON.stringify(existingProfile) !==
                      JSON.stringify(localDataParsed);
                    if (isDifferent) {
                      setConflictingEmail(formData.email);
                      setShowProfileExistsModal(true);
                    }
                  }
                  // If profile doesn't have substantial data, don't show modal - user is likely continuing their current session
                }
                // If existingProfile is null/empty, it means no profile exists on server
                // So we don't show the modal - user can continue with their new profile
              })
              .catch((error) => {
                // If API call fails, don't show modal - assume no existing profile
                console.error("Error checking existing profile:", error);
              });
          }
        } catch (error) {
          console.error("Error in profile check logic:", error);
        }
      }, 1000); // 1 second debounce

      emailCheckTimeoutRef.current = timeout;

      // Cleanup timeout on unmount or email change
      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }
  }, [formData.email]);

  // Note: Autosave is now handled by the useAutoSave hook for better UX
  // This removes redundant autosave logic and console logging

  // Handle email exists modal actions
  const handleEmailExistsModalClose = () => {
    setShowEmailExistsModal(false);
    setConflictingEmail("");
  };

  const handleUseDifferentEmail = () => {
    setShowEmailExistsModal(false);
    setConflictingEmail("");
    // Clear the email field to allow user to enter a different one
    setValue("email", "");
    // Focus on email field
    const emailInput = document.getElementById("email");
    if (emailInput) {
      emailInput.focus();
    }
    toast("Please enter a different email address");
  };

  const handleCompleteExisting = async () => {
    setShowEmailExistsModal(false);

    if (conflictingEmail) {
      try {
        const existingProfile = await fetchExistingProfile(conflictingEmail);

        if (existingProfile) {
          // Populate form with existing profile data
          Object.entries(existingProfile).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              // Ensure socialMedia is always an object, not an array
              if (key === "socialMedia" && Array.isArray(value)) {
                setValue(key as any, {});
              } else if (key === "address" && Array.isArray(value)) {
                // Ensure address is an object, not an array
                setValue(key as any, {});
              } else {
                setValue(key as any, value as any);
              }
            }
          });

          resolveSelectedTemplateFromExhibitId(
            (existingProfile as any).templateExhibitId,
            (existingProfile as any).selectedTemplate,
          );

          toast.success("Existing profile data loaded successfully!");

          // Clear the conflicting email state
          setConflictingEmail("");
        } else {
          toast.error("Could not load existing profile data");
        }
      } catch (error) {
        console.error("Error fetching existing profile:", error);
        toast.error("Failed to load existing profile data");
      }
    }
  };

  // Handle profile exists modal actions (when profile is found during email check)
  const handleProfileExistsModalClose = () => {
    console.log("Closing profile exists modal");
    setShowProfileExistsModal(false);
    setConflictingEmail("");
  };

  const handleContinueExistingProfile = async () => {
    setShowProfileExistsModal(false);

    if (conflictingEmail) {
      try {
        const existingProfile = await fetchExistingProfile(conflictingEmail);

        if (existingProfile) {
          // Populate form with existing profile data
          Object.entries(existingProfile).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              // Ensure socialMedia is always an object, not an array
              if (key === "socialMedia" && Array.isArray(value)) {
                setValue(key as any, {});
              } else if (key === "address" && Array.isArray(value)) {
                // Ensure address is an object, not an array
                setValue(key as any, {});
              } else {
                setValue(key as any, value as any);
              }
            }
          });

          resolveSelectedTemplateFromExhibitId(
            (existingProfile as any).templateExhibitId,
            (existingProfile as any).selectedTemplate,
          );

          toast.success("Existing profile data loaded successfully!");

          // Clear the conflicting email state
          setConflictingEmail("");
        } else {
          toast.error("Could not load existing profile data");
        }
      } catch (error) {
        console.error("Error fetching existing profile:", error);
        toast.error("Failed to load existing profile data");
      }
    }
  };

  const handleStartFresh = () => {
    setShowProfileExistsModal(false);
    setConflictingEmail("");
    resetFormData();
    toast("Starting with a fresh profile");
  };

  // Handle login modal actions
  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      const response = await apiLogin(email, password);

      if (response.success) {
        const token = String(response.token);

        setAuthToken(token);
        setAuth(token, { email });
        toast.success("Login successful! Loading your profile...");
        setShowLoginModal(false);
        
        // Skip payment step since user is now authenticated
        // Navigate to Template Selection (step 1)
        setTimeout(() => {
          stepNavigation.goToStep(1); // Go to Template Selection (step 1)
        }, 100);

        const location = await getLocation(email);
        // console.log("Location:", location);
        if (location.success) {
          // First, extract address-related fields from the incoming profileProperties
          const addressFields = [
            "streetAddress1",
            "streetAddress2",
            "city",
            "state",
            "zipCode",
          ];
          const address: Record<string, any> = {};

          Object.entries(location.profileProperties).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              // Collect address fields separately
              if (addressFields.includes(key)) {
                address[key] = value;
              }
              if (key === "reviews") {
                setValue("reviews" as any, value as any);
              }
              if (key === "templateId") {
                setValue("template" as any, value as any);
              } else {
                setValue(key.replace("Url", "") as any, value as any);
              }
            }
          });

          // If any address fields were found, set the address object in the form
          if (Object.keys(address).length > 0) {
            setValue(
              "address" as any,
              {
                streetAddress1: address.streetAddress1 || "",
                streetAddress2: address.streetAddress2 || "",
                city: address.city || "",
                state: address.state || "",
                zipCode: address.zipCode || "",
              } as any,
            );
          }
          resolveSelectedTemplateFromExhibitId(
            (location.profileProperties as any).templateExhibitId,
            (location.profileProperties as any).selectedTemplate,
          );
          setValue("isEditing", true);
          toast.success("Profile parsed successfully!");
        }
      } else {
        if (response.error && response.error.includes("Wrong email/password combination ")) {
          setLoginError("Wrong email/password combination. Please check your credentials.");  
        } else {
          setLoginError("Login failed. Please check your credentials.");
        }
      }
    } catch (error) {
      setLoginError("Login failed. Please check your credentials and try again.");
    }
  };

  const handleContinueWithPayment = async (email: string) => {
    try {
      // Set the email in the form
      setValue("email", email);
      setCustomerEmail(email);
      
      // Mark payment as completed in localStorage
      localStorage.setItem("paymentStatus", btoa("completed"));
      localStorage.setItem("customerEmail", email);
      // check online payment status and account status
      toast.success("Payment verified! Continuing with profile creation...");
      setShowLoginModal(false);
      
      // Navigate to Template Selection (step 2) since payment is already completed
      // For users who have already paid, we skip the payment step
      setTimeout(() => {
        stepNavigation.goToStep(2); // Go to Template Selection (step 2)
      }, 100);
    } catch (error) {
      console.error("Continue with payment error:", error);
      setLoginError("Failed to continue with payment. Please try again.");
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    logout();
    // Clear payment status so user must pay again after logout
    localStorage.removeItem("paymentStatus");
    localStorage.removeItem("customerEmail");
    localStorage.removeItem("appLink");
    resetFormData();
    stepNavigation.goToStep(1);
    toast.success("Logged out. You will need to complete payment to access your profile.");
  };
  // Handle organization exists modal actions (when saveProfile returns organization exists error)
  const handleOrganizationExistsModalClose = () => {
    setShowOrganizationExistsModal(false);
    setConflictingEmail("");
  };

  const handleOrganizationExistsUseDifferentEmail = () => {
    setShowOrganizationExistsModal(false);
    // Clear the email field to allow user to enter a different one
    setValue("email", "");
    // Focus on email field
    const emailInput = document.getElementById("email");
    if (emailInput) {
      emailInput.focus();
    }
    toast("Please enter a different email address");
  };

  // Load saved data on component mount
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData) {
      // Check if there's meaningful existing data
      const hasData = Object.values(savedData).some(
        (value) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          (typeof value === "string" ? value.trim() !== "" : true),
      );

      if (hasData) {
        Object.entries(savedData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            // Ensure socialMedia is always an object, not an array
            if (key === "socialMedia" && Array.isArray(value)) {
              setValue(key as any, {});
            } else if (key === "address" && Array.isArray(value)) {
              // Ensure address is an object, not an array
              setValue(key as any, {});
            } else {
              setValue(key as any, value as any);
            }
          }
        });
        resolveSelectedTemplateFromExhibitId(
          (savedData as any).templateExhibitId,
          (savedData as any).selectedTemplate,
        );
        toast.success("Previous form data loaded");
      }
    }

    // Payment is now compulsory for all users
    // No need to check URL parameters

    // Check if user has already completed payment and profile creation
    // Only check payment status for non-authenticated users (new profiles)
    if (!isAuthenticated) {
      const checkUserPaymentStatus = localStorage.getItem("paymentStatus");
      
      // Check if payment is completed by decoding the stored value
      let hasCompletedPayment = false;
      if (checkUserPaymentStatus) {
        try {
          const decodedStatus = atob(checkUserPaymentStatus);
          // Check for various completion indicators
          hasCompletedPayment = decodedStatus === "completed" || 
                                decodedStatus === "user just paid" ||
                                decodedStatus === atob("ZFhObGNpQnFkWE4wSUhCaGFXUT0=");
        } catch (error) {
          // If decoding fails, check if it's a direct completion indicator
          hasCompletedPayment = checkUserPaymentStatus === "completed" || 
                                checkUserPaymentStatus === "user just paid";
        }
      }
      
      if (hasCompletedPayment && !hasHandledSuccess) {
        const storedEmail = localStorage.getItem("customerEmail");
        if (storedEmail) {
          setCustomerEmail(storedEmail);
          setHasHandledSuccess(true);
          // Navigate to template selection step (step 2) after payment completion
          setTimeout(() => {
            stepNavigation.goToStep(2);
          }, 100);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFromLocalStorage, setValue, includePaymentStep]);

  // Auto-skip payment step if user has already paid
  useEffect(() => {
    if (!isAuthenticated && includePaymentStep && currentStep === 1) {
      const checkUserPaymentStatus = localStorage.getItem("paymentStatus");
      
      // Check if payment is completed by decoding the stored value
      let hasCompletedPayment = false;
      if (checkUserPaymentStatus) {
        try {
          const decodedStatus = atob(checkUserPaymentStatus);
          // Check for various completion indicators
          hasCompletedPayment = decodedStatus === "completed" || 
                                decodedStatus === "user just paid" ||
                                decodedStatus === atob("ZFhObGNpQnFkWE4wSUhCaGFXUT0=");
        } catch (error) {
          // If decoding fails, check if it's a direct completion indicator
          hasCompletedPayment = checkUserPaymentStatus === "completed" || 
                                checkUserPaymentStatus === "user just paid";
        }
      }
      
      if (hasCompletedPayment) {
        // User has already paid, skip to template selection
        setTimeout(() => {
          stepNavigation.goToStep(2);
        }, 100);
      }
    }
  }, [currentStep, isAuthenticated, includePaymentStep, stepNavigation]);

  const getStepValidationFields = (step: number): (keyof FormSchema)[] => {
    // When payment is first step, adjust the step numbers
    if (includePaymentStep && !isAuthenticated) {
      switch (step) {
        case 1: // Payment step - no validation needed
          return [];
        case 2: // Template Selection
          return ["selectedTemplate"];
        case 3: // Customization
          return ["primaryColor", "secondaryColor", "buttonColor"];
        case 4: // About Me (merged Basic & Contact)
          return ["firstName", "lastName", "headshot", "email", "phone", "socialMedia", "aboutMeText", "service"];
        case 5: // Reviews
          return ["reviews"];
        case 6: // Success step
          return [];
        default:
          return [];
      }
    } else {
      // For authenticated users or non-payment flow
      switch (step) {
        case 1: // Template Selection
          return ["selectedTemplate"];
        case 2: // Customization
          return ["primaryColor", "secondaryColor", "buttonColor"];
        case 3: // About Me (merged Basic & Contact)
          return ["firstName", "lastName", "headshot", "email", "phone", "socialMedia", "aboutMeText", "service"];
        case 4: // Reviews
          return ["reviews"];
        case 5: // Success step
          return [];
        default:
          return [];
      }
    }
  };

  // Function to get the step number for a given field name
  const getStepForField = (fieldName: keyof FormSchema): number => {
    // Handle nested social media fields (e.g., "socialMedia.facebook", "socialMedia.instagram")
    if (typeof fieldName === 'string' && fieldName.startsWith('socialMedia.')) {
      return includePaymentStep && !isAuthenticated ? 4 : 3; // About Me step
    }
    
    if (includePaymentStep && !isAuthenticated) {
      // With payment step included for non-authenticated users
      if (fieldName === "selectedTemplate") return 2;
      if (["primaryColor", "secondaryColor", "buttonColor"].includes(fieldName)) return 3;
      if (["firstName", "lastName", "headshot", "email", "phone", "socialMedia", "aboutMeText", "service"].includes(fieldName)) return 4;
      if (fieldName === "reviews") return 5;
    } else {
      // For authenticated users or without payment step
      if (fieldName === "selectedTemplate") return 1;
      if (["primaryColor", "secondaryColor", "buttonColor"].includes(fieldName)) return 2;
      if (["firstName", "lastName", "headshot", "email", "phone", "socialMedia", "aboutMeText", "service"].includes(fieldName)) return 3;
      if (fieldName === "reviews") return 4;
    }
    return 1; // Default fallback
  };

  // Function to find the first step with validation errors
  const findFirstInvalidStep = (errors: any): number => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return currentStep;

    // Find the step number for each field with errors
    const stepsWithErrors = errorFields.map(field => {
      // Handle nested errors (e.g., socialMedia.facebook, reviews.0.name)
      if (typeof field === 'string') {
        // Check if it's a nested social media field
        if (field.startsWith('socialMedia.')) {
          return getStepForField('socialMedia' as keyof FormSchema);
        }
        // Check if it's a nested review field
        if (field.startsWith('reviews.')) {
          return getStepForField('reviews' as keyof FormSchema);
        }
        // Check if it's a nested address field
        if (field.startsWith('address.')) {
          return getStepForField('email' as keyof FormSchema); // Address is part of contact info
        }
      }
      return getStepForField(field as keyof FormSchema);
    });
    
    // Return the lowest step number (first step with errors)
    return Math.min(...stepsWithErrors);
  };

  const handleNext = async () => {
    // If we're on the payment step, don't proceed - payment must be completed first
    if (includePaymentStep && currentStep === 1 && !isAuthenticated) {
      setErrorDetails({
        title: "Payment Required",
        message: "Please complete your payment before continuing.",
        details:
          "You must complete the payment to activate your profile and continue with the setup.",
      });
      setShowErrorDialog(true);
      return;
    }

    const fieldsToValidate = getStepValidationFields(currentStep);

    const isValid = await methods.trigger(fieldsToValidate);
    if (isValid) {
      nextStep();
      saveToServer({ ...formData, isEditing: isAuthenticated });
    } else {
      // Filter errors to only show those for the current step
      const stepErrors = Object.keys(errors).reduce((acc, key) => {
        if (fieldsToValidate.includes(key as keyof FormSchema)) {
          acc[key] = errors[key as keyof FormSchema];
        }
        return acc;
      }, {} as any);

      handleFormValidationErrors(
        stepErrors,
        "Please Complete This Step",
        "Found",
        "that need attention before continuing.",
      );
    }
  };

  const handlePrev = () => {
    prevStep();
  };

  // Helper function to clean empty values from form data
  const cleanFormData = (data: any): any => {
    const cleaned = { ...data };

    // Clean address object
    if (cleaned.address) {
      const cleanedAddress = Object.entries(cleaned.address).reduce(
        (acc, [key, val]) => {
          if (val && typeof val === "string" && val.trim() !== "") {
            acc[key] = val;
          }
          return acc;
        },
        {} as any,
      );

      // Always keep address as an object; if no values, use empty object
      cleaned.address =
        Object.keys(cleanedAddress).length > 0 ? cleanedAddress : {};
    } else if (Array.isArray(cleaned.address)) {
      // Coerce arrays to empty object
      cleaned.address = {};
    }

    // Clean other optional string fields
    const optionalFields = [
      "website",
      "title",
      "organizationName",
      "referralName",
      "aboutMeText",
      "service",
    ];
    optionalFields.forEach((field) => {
      if (cleaned[field] && cleaned[field].trim() === "") {
        cleaned[field] = undefined;
      }
    });

    // Clean socialMedia object
    if (cleaned.socialMedia && typeof cleaned.socialMedia === "object") {
      const cleanedSocialMedia = Object.entries(cleaned.socialMedia).reduce(
        (acc, [key, val]) => {
          if (val && typeof val === "string" && val.trim() !== "") {
            acc[key] = val;
          }
          return acc;
        },
        {} as any,
      );

      cleaned.socialMedia =
        Object.keys(cleanedSocialMedia).length > 0 ? cleanedSocialMedia : {};
    }

    return cleaned;
  };

  const onSubmit = async (data: FormSchema) => {
    const cleanedData = cleanFormData(data);
    setIsSubmitting(true);
    try {
      // Check if we're on the Reviews step (where submission should happen)
      const isReviewsStep = stepNavigation.isReviewsStep(currentStep);
      if (!isReviewsStep) {
        setErrorDetails({
          title: "Incomplete Form",
          message: "Please complete all steps before submitting.",
          details: `You are currently on step ${currentStep}. Please complete all steps to submit your profile.`,
        });
        setShowErrorDialog(true);
        setIsSubmitting(false);
        return;
      }

      // Validate template selection specifically
      if (!data.selectedTemplate || data.selectedTemplate.trim() === "") {
        setErrorDetails({
          title: "Template Required",
          message: "Please select a template before submitting.",
          details:
            "You must choose a template from the available options to create your profile.",
        });
        setShowErrorDialog(true);
        setIsSubmitting(false);
        return;
      }

      // Validate all required fields before submission
      const allFieldsValid = await methods.trigger();
      if (!allFieldsValid) {
        handleFormValidationErrors(
          errors,
          "Please Fix These Issues",
          "Found",
          "that need attention.",
        );
        setIsSubmitting(false);
        return;
      }

      // change profile status to completed
      const tempData = {
        ...cleanedData,
        status: "completed",
        isCompleted: true,
      };
      saveToServer(tempData, false);

      // Prepare data for API
      const submitData: ProfileFormData = {
        ...cleanedData,
        addContact: generateVCard(cleanedData as ProfileFormData),
        textLink: cleanedData.phone ? `sms:${cleanedData.phone}` : "",
        callLink: cleanedData.phone ? `tel:${cleanedData.phone}` : "",
        sharingMetadata: generateSharingMetadata(
          cleanedData as ProfileFormData,
        ),
        profileType:
          clientType === "Onboarding" || clientType === "KlinTeam"
            ? "singlePage"
            : "fullApp",
        /**
         * Ensure 'supportedLanguages' always includes 'en' as the first language.
         * - If 'cleanedData.supportedLanguages' is a non-empty array:
         *    - If it already includes 'en', use the array as-is.
         *    - Otherwise, add 'en' to the front, removing any duplicate 'en'.
         * - If not present or empty, default to ['en'].
         */
        supportedLanguages:
          Array.isArray(cleanedData.supportedLanguages) &&
          cleanedData.supportedLanguages.length > 0
            ? cleanedData.supportedLanguages.includes("en")
              ? cleanedData.supportedLanguages
              : [
                  "en",
                  ...cleanedData.supportedLanguages.filter(
                    (lang: string) => lang !== "en",
                  ),
                ]
            : ["en"],
        isEditing: isAuthenticated,
      };

      const response = await submitProfile(submitData);

      if (response.success && response.appLink) {
        localStorage.setItem("appLink", response.appLink);
        clearSavedData(); // Clear saved data after successful submission
        setCustomerEmail(data.email);
        // Proceed to next step (Success Step)
        nextStep();
        toast.success("Profile created successfully!");
      } else {
        // Handle specific server errors with user-friendly messages
        let userFriendlyTitle = "Profile Creation Failed";
        let userFriendlyMessage =
          response.error ||
          "We couldn't create your profile right now. Please try again.";
        let userFriendlyDetails =
          "This might be due to a temporary server issue or network problem. Please check your internet connection and try again in a few moments.";

        // Check for specific error patterns and provide better guidance
        if (response.error?.includes("already exists")) {
          userFriendlyTitle = "Email Already Registered";
          userFriendlyMessage =
            "An account with this email address already exists.";
          userFriendlyDetails =
            "Please use a different email address or contact support if you believe this is an error.";
        } else if (response.error?.includes("validation")) {
          userFriendlyTitle = "Invalid Information";
          userFriendlyMessage =
            "Some of the information provided is not valid.";
          userFriendlyDetails = "Please check your form data and try again.";
        } else if (
          response.error?.includes("unauthorized") ||
          response.error?.includes("401")
        ) {
          userFriendlyTitle = "Authentication Required";
          userFriendlyMessage = "You need to be logged in to create a profile.";
          userFriendlyDetails = "Please log in and try again.";
        } else if (
          response.error?.includes("forbidden") ||
          response.error?.includes("403")
        ) {
          userFriendlyTitle = "Access Denied";
          userFriendlyMessage =
            "You don't have permission to create a profile.";
          userFriendlyDetails = "Please contact support for assistance.";
        } else if (
          response.error?.includes("not found") ||
          response.error?.includes("404")
        ) {
          userFriendlyTitle = "Service Unavailable";
          userFriendlyMessage =
            "The profile creation service is currently unavailable.";
          userFriendlyDetails = "Please try again later or contact support.";
        } else if (
          response.error?.includes("500") ||
          response.error?.includes("internal server error")
        ) {
          userFriendlyTitle = "Server Error";
          userFriendlyMessage = "We're experiencing technical difficulties.";
          userFriendlyDetails =
            "This may be caused by using a same email address for multiple profiles. Please use a different email address or contact support if you believe this is an error.";
        }

        setErrorDetails({
          title: userFriendlyTitle,
          message: userFriendlyMessage,
          error: response.error,
          details: userFriendlyDetails,
        });
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error("Submission error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";

      // Provide more specific error messages based on error type
      let userFriendlyMessage =
        "We couldn't create your profile right now. Please try again.";
      let userFriendlyDetails =
        "This might be due to a network issue or server problem. Please check your internet connection.";

      if (errorMessage.includes("fetch") || errorMessage.includes("network")) {
        userFriendlyMessage = "Network connection issue detected.";
        userFriendlyDetails =
          "Please check your internet connection and try again. If the problem persists, try refreshing the page.";
      } else if (errorMessage.includes("timeout")) {
        userFriendlyMessage = "Request timed out.";
        userFriendlyDetails =
          "The server took too long to respond. Please try again in a few moments.";
      }

      setErrorDetails({
        title: "Connection Issue",
        message: userFriendlyMessage,
        error: errorMessage,
        details: userFriendlyDetails,
      });
      setShowErrorDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    setPaymentKey((prev) => prev + 1); // Force fresh component next time
    toast.success("Payment completed! Your profile is now active.");

    // Set customer email from form data
    setCustomerEmail(formData.email);
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    setPaymentKey((prev) => prev + 1); // Force fresh component next time
    // Don't reset appLink here so user can try payment again
    toast.error(
      'Payment was cancelled. You can try again by clicking "Complete Payment".',
    );

    // If we're on the payment step, stay on payment step (can't go back from step 1)
    if (includePaymentStep && currentStep === 1) {
      // Stay on payment step - user must complete payment to continue
    }
  };

  const resetFormData = () => {
    localStorage.removeItem("appLink");
    // Reset form to default values with all fields
    reset({
      firstName: "",
      lastName: "",
      title: "",
      organizationName: "",
      headshot: null,
      wasReferred: undefined,
      referralName: "",
      email: "",
      phone: "",
      website: "",
      address: {
        streetAddress1: "",
        streetAddress2: "",
        city: "",
        state: "",
        zipCode: "",
      },
      socialMedia: {},
      aboutMeText: "",
      service: "",
      customButtons: [],
      reviews: [],
      primaryColor: "#5855f8",
      secondaryColor: "#ee4a8c",
      buttonColor: "#42BCF5",
      colorScheme: {
        primary: "#5855f8",
        secondary: "#ee4a8c",
        button: "#42BCF5",
      },
      qrCodeLogo: null,
      fabButtonLogo: null,
      faviconLogo: null,
      supportedLanguages: [],
      selectedTemplate: "",
      templateExhibitId: "",
      template: "",
    });

    // Reset step navigation to first step
    resetToFirstStep();

    // Clear any saved data
    clearSavedData();
    setShowErrorDialog(false);
    setShowEmailExistsModal(false);
    setShowProfileExistsModal(false);
    setShowOrganizationExistsModal(false);
    setShowDataConflictModal(false);
    setShowLoginModal(false);
    setConflictingEmail("");
    setHasHandledSuccess(false);
    setCustomerEmail("");
    localStorage.removeItem("paymentStatus");
    localStorage.removeItem("customerEmail");

    // Show success message
    toast.success("Form reset successfully! Ready for new profile creation.");
  };

  const handleCloseErrorDialog = () => {
    setShowErrorDialog(false);
  };

  const handleRetrySubmission = () => {
    setShowErrorDialog(false);
    // Trigger form submission again
    methods.handleSubmit(onSubmit as any)();
  };

  // Convert FieldErrors to string-based errors for the BasicInfo component
  const stringErrors: { [key: string]: string } = {};
  Object.keys(errors).forEach((key) => {
    const error = errors[key as keyof FormSchema];
    if (error?.message && typeof error.message === "string") {
      stringErrors[key] = error.message;
    }
    // Handle nested social media errors
    if (key === "socialMedia" && error && typeof error === "object") {
      Object.keys(error).forEach((platform) => {
        const platformError = (error as any)[platform];
        if (
          platformError?.message &&
          typeof platformError.message === "string"
        ) {
          stringErrors[`socialMedia.${platform}`] = platformError.message;
        }
      });
    }
  });

    useEffect(() => {
      if (loginError) {
        setTimeout(() => {
          setLoginError("");
        }, 5000);
      }
    }, [loginError]);


  const mainForm = () => {
    return (
      <div className="form-background">
        <div className="custom-form-container">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="gradient-text text-4xl font-bold m-0">
              Professional Profile Builder
            </h1>
            {/* <p className="text-gray-600 mt-4 mb-0">
              Create your digital business card step by step
            </p>

            <div className="header-divider"></div> */}
            <div className="mt-4 template-login-container">
              {isAuthenticated ? (
                <p className="template-login-text">
                  Signed in as {user?.email}
                  <button
                    onClick={handleLogout}
                    className="template-login-link ml-2"
                  >
                    Logout
                  </button>
                </p>
              ) : (
                stepNavigation.getCurrentStepName(currentStep) ===
                  "Payment" && (
                  <p className="template-login-text">
                    Already have an account?{" "}
                    <button
                      onClick={handleLoginClick}
                      className="template-login-link"
                    >
                      Login
                    </button>
                  </p>
                )
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <ProgressBar
            currentStep={currentStep}
            totalSteps={totalSteps}
            visibleStepNumber={stepNavigation.getVisibleStepNumber(currentStep)}
            totalVisibleSteps={stepNavigation.getTotalVisibleSteps()}
            stepName={stepNavigation.getCurrentStepName(currentStep)}
            stepNavigation={stepNavigation}
            isAuthenticated={isAuthenticated}
            specialStep={
              stepNavigation.isSuccessStep(currentStep)
            }
          />

          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit as any, (formErrors) => {
                if (formErrors?.headshot) {
                  setErrorDetails({
                    title: "Headshot Required",
                    message: "Please upload a headshot before submitting.",
                    details:
                      "You must upload a headshot image to create your profile.",
                  });
                  setShowErrorDialog(true);
                  stepNavigation.goToStep(includePaymentStep ? 4 : 3);
                }
              })}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Payment Step - Show only on step 1 when payment is required and user hasn't paid */}
                  {(() => {
                    const shouldShowPayment = includePaymentStep &&
                      currentStep === 1 &&
                      !isAuthenticated;
                    
                    return shouldShowPayment && (
                      <PaymentStep
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentCancel={handlePaymentCancel}
                      />
                    );
                  })()}

                  {/* Template Selection - Show only when it's the current step */}
                  {stepNavigation.shouldShowStep(
                    "TemplateSelection",
                    currentStep
                  ) && (
                    <TemplateSelection
                      formData={formData}
                      onUpdate={(field, value) =>
                        setValue(field as keyof FormSchema, value)
                      }
                      errors={stringErrors}
                      clientType={clientType}
                    />
                  )}

                  {/* Customization - Show only when it's the current step */}
                  {stepNavigation.shouldShowStep(
                    "Customization",
                    currentStep
                  ) && (
                    <Customization
                      formData={formData}
                      onUpdate={(field, value) =>
                        setValue(field as keyof FormSchema, value)
                      }
                      errors={stringErrors}
                    />
                  )}

                  {/* About Me (merged Basic & Contact) - Show only when it's the current step */}
                  {stepNavigation.shouldShowStep("AboutMe", currentStep) && (
                    <AboutMe
                      formData={formData}
                      onUpdate={(field, value) =>
                        setValue(field as keyof FormSchema, value)
                      }
                      errors={stringErrors}
                      setIsEmailBeingChanged={setIsEmailBeingChanged}
                    />
                  )}

                  {/* Reviews - Show only when it's the current step */}
                  {stepNavigation.shouldShowStep("Reviews", currentStep) && (
                    <>
                      <Reviews
                        formData={formData}
                        onUpdate={(field, value) =>
                          setValue(field as keyof FormSchema, value)
                        }
                        errors={stringErrors}
                      />
                      {/* Preview Modal */}
                      <TemplatePreviewModal
                        isOpen={showPreview}
                        onClose={handleClosePreview}
                        formData={formData}
                        handleCreateProfile={handleSubmit(
                          onSubmit as any,
                          (formErrors) => {
                            console.log("formErrors", formErrors);
                            toast.error("Error:"+ JSON.stringify(formErrors));

                            if (formErrors?.phone) {
                              toast.error("Phone number is required");
                              stepNavigation.goToStep(3);
                            }
                            if (formErrors?.email) {
                              toast.error("Email is required");
                              stepNavigation.goToStep(3);
                            }
                            if (formErrors?.firstName) {
                              toast.error("First name is required");
                              stepNavigation.goToStep(3);
                            }
                            if (formErrors?.lastName) {
                              toast.error("Last name is required");
                              stepNavigation.goToStep(3);
                            }
                            if (formErrors?.socialMedia) {
                              toast.error("Social media is required");
                              stepNavigation.goToStep(3);
                            }
                            if (formErrors?.aboutMeText) {
                              toast.error("About me text is required");
                              stepNavigation.goToStep(3);
                            }
                            if (formErrors?.service) {
                              toast.error("Service is required");
                              stepNavigation.goToStep(3);
                            }
                            if (formErrors?.headshot) {
                              setErrorDetails({
                                title: "Headshot Required",
                                message:
                                  "Please upload a headshot before submitting.",
                                details:
                                  "You must upload a headshot image to create your profile.",
                              });
                              setShowErrorDialog(true);
                              stepNavigation.goToStep(
                                includePaymentStep ? 4 : 3
                              );
                            }
                          }
                        )}
                        isAuthenticated={isAuthenticated}
                        isSubmitting={isSubmitting}
                      />
                    </>
                  )}

                  {/* Success Step - Show only when it's the current step */}
                  {stepNavigation.shouldShowStep("Success", currentStep) && (
                    <SuccessStep
                      customerEmail={customerEmail}
                      resetFormData={resetFormData}
                      isEditing={isAuthenticated}
                      logout={logout}
                    />
                  )}

                  {/* Add other steps here as we create them */}
                  {currentStep > (includePaymentStep ? 9 : 8) && (
                    <div className="form-step active">
                      <h2 className="section-title">
                        Step {currentStep} - Coming Soon
                      </h2>
                      <p className="text-center text-gray-600">
                        This step is being migrated from the HTML version.
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              {!stepNavigation.shouldShowStep("Success", currentStep) && (
                <div className="step-navigation">
                  <div className="flex-1">
                    {canGoPrev && (
                      <GradientButton
                        variant="secondary"
                        onClick={handlePrev}
                        disabled={!canGoPrev}
                      >
                        Previous
                      </GradientButton>
                    )}
                  </div>

                  <div className="flex-1 text-center">
                    {/* Step indicator could go here */}
                  </div>

                  <div className="flex-1 text-right">
                    {/* Show Preview button on the last form step (Reviews) */}
                    {stepNavigation.isReviewsStep(currentStep) ? (
                      <GradientButton
                        variant="primary"
                        onClick={handleOpenPreview}
                        disabled={isSubmitting}
                      >
                        Preview
                      </GradientButton>
                    ) : canGoNext ? (
                      includePaymentStep &&
                      currentStep === 1 &&
                      !isAuthenticated ? (
                        <></>
                      ) : (
                        <GradientButton
                          variant="primary"
                          onClick={handleNext}
                          disabled={
                            !canGoNext ||
                            (includePaymentStep &&
                              currentStep === 1 &&
                              !isAuthenticated)
                          }
                        >
                          Next
                        </GradientButton>
                      )
                    ) : (
                      <GradientButton
                        variant="primary"
                        onClick={handleNext}
                        disabled={true}
                      >
                        Next
                      </GradientButton>
                    )}
                  </div>
                </div>
              )}
            </form>
          </FormProvider>

          {/* Loading Modal */}
          <LoadingModal
            isOpen={isSubmitting}
            message={`Creating Your Digital Card...`}
          />

          {/* Error Dialog */}
          <ErrorDialog
            isOpen={showErrorDialog}
            onClose={handleCloseErrorDialog}
            onRetry={handleRetrySubmission}
            title={errorDetails.title}
            message={errorDetails.message}
            error={errorDetails.error}
            details={errorDetails.details}
            showRetry={
              errorDetails.title === "Submission Failed" ||
              errorDetails.title === "Network Error"
            }
          />

          {/* Payment Modal */}
          {showPayment && (
            <div
              key={`payment-modal-${paymentKey}`}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      Complete Payment
                    </h2>
                    <button
                      onClick={handlePaymentCancel}
                      className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                    >
                      ×
                    </button>
                  </div>

                  <div className="mb-6">
                    <p className="text-gray-600 mb-4">
                      Complete your payment to activate your{" "}
                      {getClientConfig(clientType).name.toLowerCase()} profile.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-800 mb-2">
                        What's included:
                      </h3>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>
                          • {getClientConfig(clientType).name} digital business
                          card
                        </li>
                        <li>• QR code for easy sharing</li>
                        <li>• Custom branding and colors</li>
                        <li>• Social media integration</li>
                      </ul>
                    </div>
                  </div>

                  <CheckoutForm
                    onSuccess={handlePaymentSuccess}
                    onCancel={handlePaymentCancel}
                  />

                  <div className="mt-6 text-center">
                    <button
                      onClick={handlePaymentCancel}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Cancel payment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Profile Exists Modal */}
          <ProfileExistsModal
            isOpen={
              showProfileExistsModal &&
              !stepNavigation.shouldShowStep("Success", currentStep) &&
              !isAuthenticated
            }
            email={conflictingEmail}
            onClose={handleProfileExistsModalClose}
            onContinue={handleContinueExistingProfile}
            onStartFresh={handleStartFresh}
          />

          {/* Organization Exists Modal */}
          <OrganizationExistsModal
            isOpen={
              showOrganizationExistsModal &&
              !stepNavigation.shouldShowStep("Success", currentStep) &&
              !isAuthenticated
            }
            email={conflictingEmail}
            onClose={handleOrganizationExistsModalClose}
            onUseDifferentEmail={handleOrganizationExistsUseDifferentEmail}
            onResetForm={resetFormData}
          />

          {/* Email Exists Modal */}
          <EmailExistsModal
            isOpen={
              showEmailExistsModal &&
              !stepNavigation.shouldShowStep("Success", currentStep)
            }
            email={conflictingEmail}
            onClose={handleEmailExistsModalClose}
            onUseDifferentEmail={handleUseDifferentEmail}
            onCompleteExisting={handleCompleteExisting}
            onResetForm={resetFormData}
          />

          {/* Login Modal */}
          <LoginModal
            isOpen={showLoginModal}
            onClose={handleLoginModalClose}
            onLogin={handleLogin}
            onContinueWithPayment={handleContinueWithPayment}
            loginError={loginError}
            setShowProfileExistsModal={setShowProfileExistsModal}
            setConflictingEmail={setConflictingEmail}
          />

          {/* AutoSave Indicator */}
          <AutoSaveIndicator
            status={autosaveStatus}
            isVisible={
              !!(
                formData.email &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
              )
            }
          />

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#363636",
                color: "#fff",
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: "#4ade80",
                  secondary: "#fff",
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#fff",
                },
              },
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/return"
          element={<Return resetFormData={resetFormData} />}
        />
        <Route path="/" element={mainForm()} />
      </Routes>
    </Router>
  );
};

export default App;
